import {
  Divider,
  Typography,
  Button,
  Show,
  InlineNotification,
  NotificationBar
} from '@sanlam-glacier/app-common/components';
import styles from './statements-form.module.scss';
import { StatementPeriodTypeOptions, StatementTypes } from '../../../types/client.enum';
import { useForm, FormProvider } from 'react-hook-form';
import { StatementModal } from '../../molecules/statement-modal/StatementModal';
import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getClientData } from 'bff/hooks/reporting/ClientInvestmentData';
import { useClientIdFromParams } from 'modules/clients/hooks/useClientIdFromParams';
import { InvestmentsDropdown } from '../../molecules/statement-form-fields-and-sub-sections/InvestmentsDropdown';
import { StatementPeriod } from '../../molecules/statement-form-fields-and-sub-sections/statement-period/StatementPeriod';
import { StatementOptions } from '../../molecules/statement-form-fields-and-sub-sections/StatementOptions';
import { StatementTypeDropdown } from '../../molecules/statement-form-fields-and-sub-sections/StatementTypeDropdown';
import { StatementFormValues } from 'modules/clients/types/client.types';
import {
  AssistiveTextBasedOnStatementType,
  validateDate
} from 'modules/clients/utils/statement-form-helper';
import { InlineBanner } from 'components/molecules/banners/inline-banner/InlineBanner';
import { SomethingWentWrong } from 'modules/app/components/pages/something-went-wrong/SomethingWentWrong';
import {
  getDateMinusDays,
  getStartAndEndDate
} from 'modules/clients/utils/statement-period-preset-helpers';

const StatementsForm = () => {
  const methods = useForm<StatementFormValues>({
    mode: 'onChange',
    defaultValues: {
      statementType: null,
      investments: [],
      statementPeriodType: null,
      dateRange: [null, null],
      periodPreset: '',
      statementOptions: []
    },
    shouldUnregister: true
  });

  const testComponentName = 'statements';
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [submittedFormValues, setSubmittedFormValues] = useState<StatementFormValues | null>(null);
  const [errorState, setErrorState] = useState(false);

  const { control, watch, setValue, handleSubmit } = methods;
  const clientId = useClientIdFromParams();

  const { data: client } = useSuspenseQuery(getClientData(clientId));

  const statementType = watch('statementType');
  const selectedInvestments = watch('investments');
  const statementPeriodType = watch('statementPeriodType');
  const [startDate, endDate] = watch('dateRange');
  const periodPreset = watch('periodPreset');
  const statementOptions = watch('statementOptions');

  const min30DaysStart = getDateMinusDays(31);

  const toCurrentDateSelected =
    statementPeriodType === StatementPeriodTypeOptions.toCurrentDate &&
    ((periodPreset && Array.isArray(periodPreset) && periodPreset.length > 0) ||
      (typeof periodPreset === 'string' && periodPreset !== ''));

  const customPeriodSelected =
    statementPeriodType === StatementPeriodTypeOptions.customPeriod &&
    startDate !== null &&
    endDate !== null;

  const standardPeriodsOptionChecked =
    Array.isArray(statementPeriodType) &&
    statementPeriodType.includes(StatementPeriodTypeOptions.standardPeriods) &&
    ((periodPreset && Array.isArray(periodPreset) && periodPreset.length > 0) ||
      (typeof periodPreset === 'string' && periodPreset !== ''));

  const customPeriodOptionSelected =
    Array.isArray(statementPeriodType) &&
    statementPeriodType.includes(StatementPeriodTypeOptions.customPeriod) &&
    startDate !== null &&
    endDate !== null;

  const bothOptionsChecked = standardPeriodsOptionChecked && customPeriodOptionSelected;

  const buttonAndStatementOptionsDisabled =
    (!statementType && typeof statementType === 'string' && statementType !== '') ||
    !(selectedInvestments.length >= 0) ||
    statementPeriodType?.length === 0 ||
    toCurrentDateSelected ||
    customPeriodSelected ||
    standardPeriodsOptionChecked ||
    customPeriodOptionSelected ||
    bothOptionsChecked;

  useEffect(() => {
    if (!startDate) {
      setValue('dateRange.0', min30DaysStart, { shouldValidate: true });
    }
    const subscription = watch((_values) => {
      if (errorState) setErrorState(false);
    });
    return () => subscription.unsubscribe();
  }, [setValue, min30DaysStart, startDate, errorState, watch]);

  const onSubmit = (data: StatementFormValues) => {
    if (data.dateRange[0] === null || data.dateRange[1] === null || toCurrentDateSelected) {
      const [start, end] = getStartAndEndDate(data);
      const validateStartDate = start ? start : min30DaysStart;
      const validEndDate = end ? end : new Date();
      setValue('dateRange', [validateStartDate, validEndDate], {
        shouldValidate: true
      });
      data.dateRange = [validateStartDate, validEndDate];
    }

    if (
      data.dateRange[0] &&
      data.dateRange[1] &&
      validateDate(data.dateRange[0], data.dateRange[1])
    ) {
      setIsStatementModalOpen(true);
      setSubmittedFormValues(data);
    } else {
      setErrorState(true);
    }
  };

  if (client === undefined) {
    return <SomethingWentWrong />;
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.formContainer}>
        <Typography
          variant={Typography.VARIANT.H3}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.INHERIT}
          className={styles.generateStatementHeading}
        >
          Generate statement
        </Typography>

        <div className={styles.statementAndInvestmentSelectionContainer}>
          <StatementTypeDropdown
            control={control}
            setValue={setValue}
            min30DaysStart={min30DaysStart}
          />

          <InvestmentsDropdown
            control={control}
            multiSelect={
              statementType !== null &&
              ![StatementTypes.transactionDetail, StatementTypes.fundDetail].includes(
                statementType as StatementTypes
              )
            }
            clientInvestments={client.investments}
            assistiveText={
              statementType !== null
                ? AssistiveTextBasedOnStatementType[statementType as StatementTypes]
                : 'Select one or more investments.'
            }
          />
        </div>

        <Divider />

        <StatementPeriod
          control={control}
          statementType={statementType}
          selectedInvestments={selectedInvestments}
          min30DaysStart={min30DaysStart}
          errorState={errorState}
          setErrorState={setErrorState}
        />

        <Divider />

        <div className={styles.statementOptionsContainer}>
          <StatementOptions
            control={control}
            statementType={statementType}
            disabled={!buttonAndStatementOptionsDisabled}
          />
          <Show
            when={
              statementType === StatementTypes.transactionDetail ||
              statementType === StatementTypes.fundDetail
            }
          >
            <InlineBanner
              status={InlineNotification.STATUS.INFO}
              heading='Internal rate of return (IRR)'
              message='IRR is not available for a transaction or fund detail statement. To include IRR, please select the transaction summary or internal rate of return statement type.'
              spacing={NotificationBar.SPACING.LOOSE}
            />
          </Show>
        </div>

        <Divider />

        <div>
          <Button
            disabled={
              !buttonAndStatementOptionsDisabled ||
              (statementType === StatementTypes.internalRateOfReturnInv &&
                (!statementOptions || statementOptions.length === 0)) ||
              errorState
            }
            data-testid={`${testComponentName}`}
            variant={Button.VARIANT.PRIMARY}
            size={Button.SIZE.SMALL}
            onClick={handleSubmit(onSubmit)}
          >
            Generate statement
          </Button>

          <StatementModal
            isOpen={isStatementModalOpen}
            onClose={() => setIsStatementModalOpen(false)}
            clientDetails={client}
            formValues={submittedFormValues}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default StatementsForm;
